import {
  Customer,
  CustomerSite,
  UpdateCustomerDocument,
  UpdateCustomerSiteDocument,
} from "@hydrologiq/gql-experience/graphql"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { useCallback } from "react"
import { useCustomer, useUpdateCustomer, useUpdateSite } from "../providers/helpers/useCustomer"

/**
 * Encapsulates renaming a customer: runs the UpdateCustomer mutation against the
 * experience (primary) endpoint and keeps the customer provider store in sync.
 * Shared by the editable page title and the Information tab.
 */
export function useRenameCustomer(customer?: Customer | null) {
  const [mutateCustomer, { loading }] = useMutationWithEndpoint(UpdateCustomerDocument, {}, "primary")
  const updateCustomer = useUpdateCustomer()

  const renameCustomer = useCallback(
    async (newName: string) => {
      const name = newName.trim()
      if (!customer || !name || name === customer.name) {
        return
      }

      try {
        const result = await mutateCustomer({ variables: { id: customer.id, name } })

        if (result.errors?.length) {
          throw result.errors[0]
        }

        updateCustomer({ name })
      } catch (error) {
        console.error("Failed to rename customer:", error)
        throw error
      }
    },
    [customer, mutateCustomer, updateCustomer],
  )

  return { renameCustomer, renaming: loading }
}

/**
 * Encapsulates renaming a customer site: runs the UpdateCustomerSite mutation
 * against the experience (primary) endpoint and keeps the provider store in
 * sync. Shared by the editable page title and the Information tab.
 */
export function useRenameSite(site?: CustomerSite | null) {
  const [mutateCustomerSite, { loading }] = useMutationWithEndpoint(
    UpdateCustomerSiteDocument,
    {},
    "primary",
  )
  const updateCustomerSite = useUpdateSite()
  const customer = useCustomer()

  const renameSite = useCallback(
    async (newName: string) => {
      const name = newName.trim()
      if (!customer || !site || !name || name === site.name) {
        return
      }

      try {
        const result = await mutateCustomerSite({
          variables: { customerId: customer.id, siteId: site.id, name },
        })

        if (result.errors?.length) {
          throw result.errors[0]
        }

        updateCustomerSite({ name })
      } catch (error) {
        console.error("Failed to rename site:", error)
        throw error
      }
    },
    [customer, site, mutateCustomerSite, updateCustomerSite],
  )

  return { renameSite, renaming: loading }
}
